import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authService } from '@/services/auth.service';
import { ssoService } from '@/services/sso.service';
import tenantStatusService from '@/services/tenant-status.service';
import { LoginRequest } from '@/types/auth.types';
import { ActiveProvider } from '@/types/sso.types';
import { useAuth } from '@/contexts/AuthContext';
import { generateRequestId, encryptAES } from '@/utils/crypto';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle, Chrome, Github, CheckCircle } from 'lucide-react';

const loginSchema = yup.object().shape({
  email: yup.string().email('Invalid email address').required('Email is required'),
  password: yup.string().required('Password is required'),
  remember_me: yup.boolean(),
});

type LoginFormData = {
  email: string;
  password: string;
  remember_me?: boolean;
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState('');
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const [registrationMessage, setRegistrationMessage] = useState<string>('');
  const [checkingTenantStatus, setCheckingTenantStatus] = useState(true);
  const [logoErrors, setLogoErrors] = useState<Record<string, boolean>>({});

  // Fetch active SSO providers
  const { data: ssoData, isLoading: loadingProviders } = useQuery({
    queryKey: ['activeProviders'],
    queryFn: () => ssoService.getActiveProviders(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Older API versions returned a bare array; newer ones wrap it alongside the setting.
  const ssoPayload = Array.isArray(ssoData) ? { providers: ssoData } : ssoData;
  const ssoProviders: ActiveProvider[] = ssoPayload?.providers ?? [];

  // Fail open: when the setting is absent or the request failed, show the form so
  // nobody is locked out of the tenant.
  const hideClassicLogin = ssoPayload?.hide_classic_login === true;

  // Fail open: when the setting is absent or the request failed, show the form so
  // nobody is locked out of the tenant.
  const showSignUpButton = ssoPayload?.show_sign_up_button === true;

  // Handle registration success state
  useEffect(() => {
    const state = location.state as { email?: string; message?: string } | undefined;
    if (state?.message) {
      setRegistrationMessage(state.message);
      // Clear the message after 10 seconds
      const timer = setTimeout(() => setRegistrationMessage(''), 10000);
      return () => clearTimeout(timer);
    }
  }, [location]);

  // Cache Spline assets only for this page; OneSignal owns root scope.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker
      .register('/spline-sw.js', { scope: '/login' })
      .catch((err) => console.warn('[Spline SW] register failed:', err));
  }, []);

  // Check tenant status before showing login form
  useEffect(() => {
    const checkTenantAccess = async () => {
      try {
        const status = await tenantStatusService.checkStatus();

        if (!status.can_login) {
          // Tenant is INACTIVE or SUSPENDED - redirect to blocked page
          console.warn(`[Login] Tenant status is ${status.status}, redirecting to /tenant-blocked`);
          navigate('/tenant-blocked', { replace: true });
          return;
        }

        // Tenant is ACTIVE - allow login
        setCheckingTenantStatus(false);
      } catch (error) {
        console.error('[Login] Failed to check tenant status:', error);
        // On error, allow login attempt - backend will validate
        setCheckingTenantStatus(false);
      }
    };

    checkTenantAccess();
  }, [navigate]);

  // GuestGuard will handle the redirect when user becomes authenticated

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: (location.state as any)?.email || '',
      remember_me: true, // Always true since we handle refresh tokens implicitly
    }
  });

  const loginMutation = useMutation({
    mutationFn: async (data: LoginFormData) => {
      // Generate request_id as AES encryption key
      const requestId = generateRequestId();

      // Encrypt username and password using AES-CBC
      const encryptedUsername = encryptAES(data.email, requestId);
      const encryptedPassword = encryptAES(data.password, requestId);

      const loginRequest: LoginRequest = {
        request_id: requestId,
        username: encryptedUsername,
        password: encryptedPassword,
      };
      const result = await login(loginRequest);
      return result;
    },
    onSuccess: async () => {
      toast.success('Welcome back!');
      // GuestGuard will handle navigation when isAuthenticated changes
    },
    onError: (error: Error) => {
      const errorMessage = error.message.toLowerCase();

      // Check for account status errors (backend handles locking)
      const isAccountStatusError =
        errorMessage.includes('inactive') ||
        errorMessage.includes('suspended') ||
        errorMessage.includes('locked') ||
        errorMessage.includes('archived') ||
        errorMessage.includes('invitation') ||
        errorMessage.includes('registration') ||
        errorMessage.includes('disabled');

      if (isAccountStatusError) {
        setLockoutMessage(error.message);
      }

      // Show toast for all errors
      toast.error(error.message, { duration: 5000 });
    },
  });

  const onSubmit = (data: LoginFormData) => {
    // Clear lockout message on new attempt
    setLockoutMessage('');
    loginMutation.mutate(data);
  };

  const handleSSOLogin = async (providerName: string) => {
    try {
      setLoadingProvider(providerName);
      
      // Show loading toast
      const loadingToast = toast.loading(`Redirecting to ${providerName}...`);
      
      // Get the return URL from query params if any
      const returnUrl = searchParams.get('redirect') || '/dashboard';
      
      // Store return URL for after SSO callback
      localStorage.setItem('sso_return_url', returnUrl);
      
      // Small delay to show loading state before redirect
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Initiate SSO login - backend will redirect to provider
      await ssoService.initiateLogin(providerName, returnUrl);
      
      toast.dismiss(loadingToast);
    } catch (error) {
      toast.error('Failed to initiate SSO login. Please try again.');
      console.error('SSO login error:', error);
      localStorage.removeItem('sso_return_url');
      setLoadingProvider(null);
    }
  };

  // Helper to get provider icon
  const getProviderIcon = (providerName: string) => {
    const icons: Record<string, JSX.Element> = {
      google: <Chrome className="w-5 h-5" />,
      github: <Github className="w-5 h-5" />,
      microsoft: <div className="w-5 h-5 text-xs font-bold">MS</div>,
      okta: <div className="w-5 h-5 text-xs font-bold">OK</div>,
    };
    return icons[providerName.toLowerCase()] || <div className="w-5 h-5 text-xs font-bold">{providerName[0].toUpperCase()}</div>;
  };

  // Show loading while checking tenant status. Waiting on the providers query too
  // avoids rendering the email/password form before we know whether it is enabled.
  if (checkingTenantStatus || loadingProviders) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* SSO Loading Overlay */}
      {loadingProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4 max-w-sm">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <div className="text-center">
              <p className="text-base lg:text-lg font-semibold text-gray-900">Redirecting to {loadingProvider}</p>
              <p className="text-sm text-gray-500 mt-2">Please wait while we redirect you to sign in</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="mb-8">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">Welcome back!</h2>
        <p className="text-gray-600 mt-2">
          Please sign in to continue your learning journey
        </p>
      </div>

      {registrationMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-green-800">{registrationMessage}</p>
          </div>
        </div>
      )}

      {lockoutMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{lockoutMessage}</p>
          </div>
        </div>
      )}

      {!hideClassicLogin && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                {...register('email')}
                type="email"
                className="input-field pl-10"
                placeholder="Enter your email"
                autoComplete="email"
                data-sentry-mask
              />
            </div>
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                className="input-field pl-10 pr-10"
                placeholder="Enter your password"
                autoComplete="current-password"
                data-sentry-mask
                data-sentry-ignore
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
          </div>

          <div className="flex items-center justify-between">
            {/* Remember me is now handled implicitly - all users get refresh tokens */}
            <div></div>
            <Link
              to="/forgot-password"
              className="text-sm text-primary-700 hover:text-primary-800"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || loginMutation.isPending}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {(isSubmitting || loginMutation.isPending) ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                Sign In
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      )}

      {/* SSO Login Options */}
      {ssoProviders.length > 0 && (
        <div className="mt-6">
          {/* Only a separator when there is a classic form above it to separate from. */}
          {!hideClassicLogin && (
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3">
            {ssoProviders.map((provider: ActiveProvider) => {
              const isLoading = loadingProvider === provider.provider_name;
              return (
                <button
                  key={provider.provider_name}
                  onClick={() => handleSSOLogin(provider.provider_name)}
                  disabled={loadingProvider !== null}
                  className={`w-full min-w-0 flex items-center justify-center gap-3 px-4 py-2.5 border rounded-lg shadow-sm transition-all ${
                    isLoading 
                      ? 'border-primary-500 bg-primary-50 cursor-wait' 
                      : loadingProvider !== null
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                      : 'border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm font-medium text-primary-700">
                        Redirecting...
                      </span>
                    </>
                  ) : (
                    <>
                      {provider.logo_url && !logoErrors[provider.provider_name] ? (
                        <img
                          src={provider.logo_url}
                          alt={provider.provider_name}
                          className="h-6 w-auto max-w-[96px] sm:max-w-[140px] object-contain shrink-0"
                          onError={() => {
                            // // If logo fails to load, show icon fallback
                            // const target = e.target as HTMLImageElement;
                            // target.style.display = 'none';
                            // target.parentElement!.insertBefore(
                            //   document.createElement('div'),
                            //   target
                            // );
                            setLogoErrors(prev => ({ ...prev, [provider.provider_name]: true }));
                          }}
                        />
                      ) : (
                        getProviderIcon(provider.provider_name)
                      )}
                      {/* May wrap: a long name next to a wide logo is wider than a
                          phone, and on one line it spilled out of both sides. */}
                      <span className="min-w-0 text-sm font-medium text-gray-700 text-left leading-snug break-words">
                        {provider.display_name}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showSignUpButton && (
      <div className="mt-6 text-center text-sm text-gray-600">
        Don't have an account?{' '}
        <Link 
          to="/register" 
          className="text-primary-700 hover:text-primary-800 font-medium"
        >
          Sign up here
        </Link>
      </div>
      )}
    </div>
  );
}